import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { Gavel as GavelIcon } from '@mui/icons-material';
import { apiCall } from '../../utils/fetchAPI';
import { useToast } from '../../context/ToastContext';
import { useOptionalAuth } from '../../context/AuthContext';
import { useModeratedCities } from '../../hooks/moderation/useModeratedCities';
import { translateApiMessage } from '../../utils/apiMessages';

const REASON_MAX_LENGTH = 500;

/**
 * Hide a piece of content straight from where it is read.
 *
 * Offered to a moderator of the content's city only, and never on their own
 * content — the API refuses both, so the button is not shown for them. Content
 * is published as written; this is what a moderator does about something they
 * come across, without filing a report to themselves first. Any report already
 * waiting on it is closed by the same decision.
 *
 * `isOwn` is the caller's to say, since each kind of content names its author
 * its own way.
 */
function ModerateDialog({ targetType, targetUuid, citySlug, isOwn = false, hidden = false, onModerated }) {
  const { isAuthenticated } = useOptionalAuth();
  const moderatedCities = useModeratedCities();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isAuthenticated || isOwn || hidden || !citySlug || !moderatedCities.includes(citySlug)) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!reason.trim()) {
      setError('Indiquez le motif : l’auteur le verra.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const response = await apiCall('/moderator/hide', {
        method: 'POST',
        body: JSON.stringify({ type: targetType, uuid: targetUuid, reason: reason.trim() }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Ce contenu n’a pas pu être masqué.');
        return;
      }

      toast.success('Contenu masqué. Son auteur voit le motif.');
      setReason('');
      close();
      onModerated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<GavelIcon fontSize="small" />}
        onClick={() => setOpen(true)}
        // Quiet like "Signaler" beside it: the theme underlines text buttons,
        // which reads as a link to somewhere else.
        sx={{ color: 'text.disabled', textDecoration: 'none', '&:hover': { color: 'warning.main', textDecoration: 'none' } }}
      >
        Modérer
      </Button>

      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>Masquer ce contenu</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Il disparaît pour les voisins. Son auteur continue de le voir, avec le motif
              ci-dessous. Un administrateur peut le rétablir.
            </DialogContentText>

            <TextField
              label="Motif"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
              autoFocus
              disabled={sending}
              inputProps={{ maxLength: REASON_MAX_LENGTH }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={sending} variant="outlined">
              Annuler
            </Button>
            <Button type="submit" variant="contained" color="warning" disabled={sending}>
              Masquer
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default ModerateDialog;
